import { gtag, sanitizeAnalyticsUrl } from '@worldcoo/ui/lib/analytics';

/**
 * Normally reached only when the title is already right; a slower page reports the previous title, since an
 * unchanged title and a late one look the same. Client-side portal titles are the ones at risk (TEC-3865).
 */
const TITLE_SETTLE_TIMEOUT_MS = 1000;

/**
 * One hit per in-app navigation, once the arriving title is in the document (TEC-3823): on the title changing,
 * the timeout, the next navigation or `pagehide`. docs/notes/portals/page-view.md
 */
export function installPageViewTracking(subscribeToNavigations: (onNavigate: (href: string) => void) => void): void {
	let pending: { href: string; stopWaiting: () => void } | undefined;

	const send = (href: string): void => {
		pending?.stopWaiting();
		pending = undefined;
		gtag('event', 'page_view', {
			// Not `state.location`, the rewritten internal path (`/www/contact`), which would change every GA4 page_location.
			page_location: sanitizeAnalyticsUrl(href),
			page_title: document.title,
		});
	};

	window.addEventListener('pagehide', () => {
		if (pending) send(pending.href);
	});

	subscribeToNavigations((href) => {
		if (pending) send(pending.href);
		// Per navigation, so one that ends on the timeout cannot mislead the next.
		const titleBeforeArrival = document.title;
		// There is no `titlechange` event; every mutation `<Seo>` makes lands in the head.
		const observer = new MutationObserver(() => {
			if (document.title !== titleBeforeArrival) send(href);
		});
		observer.observe(document.head, { subtree: true, childList: true, characterData: true });
		const timer = window.setTimeout(() => send(href), TITLE_SETTLE_TIMEOUT_MS);
		pending = {
			href,
			stopWaiting: () => {
				observer.disconnect();
				window.clearTimeout(timer);
			},
		};
	});
}
